/* eslint css-modules/no-unused-class: off */

import { CSSInterpolation, CSSObject } from "@emotion/css/create-instance";
import { APISite } from "../../server/types/index.js";
import accommodationFeaturesModuleStyles from "../stylesheets/components/accommodation-features-module.module.css";
import footerModuleStyles from "../stylesheets/components/footer-module.module.css";
import imageOverlayStyles from "../stylesheets/components/image-overlay.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import moduleConsentBoxStyles from "../stylesheets/components/module-consent-box.module.css";
import multiBoxStyles from "../stylesheets/components/multi-box.module.css";
import { Fonts, ThreeSizes } from "../types/index.js";
import { round } from "./utils.js";

type FontType = "title" | "body" | "dynamic";

const getFontSizeRules = ({
  isPreview,
  baseFontSize,
  fontScale,
  sizeSetting,
  smallerOnNarrow,
}: {
  isPreview: boolean;
  baseFontSize: number;
  fontScale: number;
  sizeSetting: ThreeSizes;
  smallerOnNarrow: boolean;
}): CSSObject => {
  const fontSize = getFontSize({ baseFontSize, fontScale, sizeSetting });
  const rules: CSSObject = {
    fontSize: round(fontSize * (smallerOnNarrow ? 0.75 : 1), 2) + "em",
    ...(smallerOnNarrow
      ? {
          [isPreview ? ".Query--large &" : "@media (min-width: 1024px)"]: {
            fontSize: round(fontSize, 2) + "em",
          },
        }
      : {}),
  };
  return rules;
};

const getFontScale = (fonts: Fonts, family: string) =>
  fonts.byFamily[family]?.scale ?? 1;

const getFontSize = ({
  baseFontSize,
  fontScale,
  sizeSetting,
}: {
  baseFontSize: number;
  fontScale: number;
  sizeSetting: ThreeSizes;
}) => {
  const sizesMap: { [key in ThreeSizes]: number } = {
    small: 0.95,
    medium: 1,
    big: 1.05,
  };

  return (
    Math.round(baseFontSize * fontScale * sizesMap[sizeSetting] * 100) / 100
  );
};

export const getDynamicTitleFontsCSS = ({
  fonts,
  site,
  isPreview,
}: {
  isPreview: boolean;
  fonts: Fonts;
  site: APISite;
}): CSSInterpolation => {
  interface FontSizeBySelector {
    [selector: string]: { size: number | undefined; type: FontType };
  }

  const fontSizeBySelector: FontSizeBySelector = {
    [`.${imageOverlayStyles.ImageOverlay__Title}`]: {
      size: 3.5,
      type: "title",
    },
    [`.${imageOverlayStyles.ImageOverlay__Subtitle}`]: {
      size: 1.6,
      type: "dynamic",
    },
    [`.${moduleBaseStyles.Module__Title}`]: { size: 2, type: "title" },
    [`h1.${moduleBaseStyles.Module__Title}`]: { size: 2.4, type: "title" },
    [`.${moduleBaseStyles.Module__Subtitle}`]: { size: 1.7, type: "dynamic" },
    [`.${multiBoxStyles.MultiBox__Tile__Headings}`]: {
      size: undefined,
      type: "title",
    },
    [`.${footerModuleStyles.FooterModule__Title}`]: {
      size: 1.3,
      type: "title",
    },
    ".Bs-Collapse__HeaderWrap": { size: 1, type: "title" },
    [`.${moduleConsentBoxStyles.ModuleConsentBox__Title}`]: {
      size: 1.5,
      type: "title",
    },
    [`.${moduleBaseStyles.Module__BodyText}`]: { size: 1.1, type: "body" },
    [`.${accommodationFeaturesModuleStyles.AccommodationFeaturesModule__List}`]:
      { size: 1.1, type: "body" },
    [`.${moduleBaseStyles.Module__BodyText} h2`]: {
      size: 1.5,
      type: "dynamic",
    },
    [`.${moduleBaseStyles.Module__BodyText} h3`]: {
      size: 1.17,
      type: "dynamic",
    },
  };

  return Object.entries(fontSizeBySelector).reduce<CSSObject>(
    (carry, [selector, { size: baseFontSize, type: fontType }]) => {
      const isTitleFont =
        (site.titleFontForSubtitle && fontType === "dynamic") ||
        fontType === "title";
      const fontScale = getFontScale(
        fonts,
        isTitleFont && site.titleFontFamily
          ? site.titleFontFamily
          : site.fontFamily,
      );

      carry[selector] = {
        fontFamily:
          isTitleFont && site.titleFontFamily
            ? `"${site.titleFontFamily}", sans-serif`
            : undefined,
        ...(baseFontSize
          ? getFontSizeRules({
              baseFontSize,
              fontScale,
              isPreview,
              sizeSetting:
                fontType === "body" ? site.fontSize : site.titleFontSize,
              smallerOnNarrow: fontType !== "body",
            })
          : {}),
      };
      return carry;
    },
    {},
  );
};
